import { useState } from 'react';
import { User, Settings, Lock, Unlock, Eye } from 'lucide-react';
import { ROLES, RoleType } from '../types';
import RoleConfigModal from './RoleConfigModal';

// 角色配置类型
interface RoleConfig {
  roleId: RoleType;
  count: number;
}

// 房间配置
interface RoomConfig {
  maxPlayers: number;
  goodRoles: {
    merlin: number;
    percival: number;
    loyal: number;
  };
  evilRoles: {
    mordred: number;
    morgana: number;
    assassin: number;
    oberon: number;
  };
  extensions: {
    ladyOfLake: boolean;
    excalibur: boolean;
  };
  forcedRound: 'none' | 'single3' | 'single5' | 'global3';
  publicVote: boolean;
}

interface RoomConfigPanelProps {
  config: RoomConfig;
  isHost: boolean;
  isLocked: boolean;
  onConfigChange?: (config: RoomConfig) => void;
  onLockConfig?: () => void;
  onUnlockConfig?: () => void;
}

// 根据人数计算角色配置
const calculateRoleConfigs = (playerCount: number): RoleConfig[] => {
  const configs: Record<number, RoleConfig[]> = {
    5: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
    ],
    6: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 2 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
    ],
    7: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 2 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'oberon', count: 1 },
    ],
    8: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 3 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'minion', count: 1 },
    ],
    9: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 4 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
    ],
    10: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 4 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'oberon', count: 1 },
    ],
    11: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 5 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'minion', count: 1 },
    ],
    12: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 5 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'oberon', count: 2 },
    ],
    13: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 6 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'oberon', count: 1 },
      { roleId: 'minion', count: 1 },
    ],
  };
  return configs[playerCount] || configs[10];
};

// 将RoleConfig数组转换为RoomConfig格式
const roleConfigsToRoomConfig = (
  playerCount: number,
  roleConfigs: RoleConfig[],
  existingConfig: RoomConfig
): RoomConfig => {
  const goodRoles = { merlin: 0, percival: 0, loyal: 0 };
  const evilRoles = { mordred: 0, morgana: 0, assassin: 0, oberon: 0 };

  roleConfigs.forEach((config) => {
    const role = ROLES[config.roleId];
    if (role.faction === 'good') {
      if (config.roleId === 'merlin') goodRoles.merlin = config.count;
      else if (config.roleId === 'percival') goodRoles.percival = config.count;
      else if (config.roleId === 'loyal') goodRoles.loyal = config.count;
    } else {
      if (config.roleId === 'mordred') evilRoles.mordred = config.count;
      else if (config.roleId === 'morgana') evilRoles.morgana = config.count;
      else if (config.roleId === 'assassin') evilRoles.assassin = config.count;
      else if (config.roleId === 'oberon') evilRoles.oberon = config.count;
    }
  });

  return {
    ...existingConfig,
    maxPlayers: playerCount,
    goodRoles,
    evilRoles,
  };
};

// 将RoomConfig转换为RoleConfig数组
const roomConfigToRoleConfigs = (config: RoomConfig): RoleConfig[] => {
  const configs: RoleConfig[] = [];
  
  // 正义阵营
  if (config.goodRoles.merlin > 0) configs.push({ roleId: 'merlin', count: config.goodRoles.merlin });
  if (config.goodRoles.percival > 0) configs.push({ roleId: 'percival', count: config.goodRoles.percival });
  if (config.goodRoles.loyal > 0) configs.push({ roleId: 'loyal', count: config.goodRoles.loyal });
  
  // 邪恶阵营
  if (config.evilRoles.mordred > 0) configs.push({ roleId: 'mordred', count: config.evilRoles.mordred });
  if (config.evilRoles.morgana > 0) configs.push({ roleId: 'morgana', count: config.evilRoles.morgana });
  if (config.evilRoles.assassin > 0) configs.push({ roleId: 'assassin', count: config.evilRoles.assassin });
  if (config.evilRoles.oberon > 0) configs.push({ roleId: 'oberon', count: config.evilRoles.oberon });

  return configs;
};

export default function RoomConfigPanel({
  config,
  isHost,
  isLocked,
  onConfigChange,
  onLockConfig,
  onUnlockConfig,
}: RoomConfigPanelProps) {
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [showRoleConfigModal, setShowRoleConfigModal] = useState(false);

  const roleConfigs = roomConfigToRoleConfigs(config);

  const updatePlayerCount = (count: number) => {
    if (!isHost || isLocked) return;
    const newRoleConfigs = calculateRoleConfigs(count);
    const newConfig = roleConfigsToRoomConfig(count, newRoleConfigs, config);
    onConfigChange?.(newConfig);
  };

  const handleRoleConfigSave = (newRoleConfigs: RoleConfig[]) => {
    const newConfig = roleConfigsToRoomConfig(config.maxPlayers, newRoleConfigs, config);
    onConfigChange?.(newConfig);
  };

  const updateGameOption = (key: 'ladyOfLake' | 'excalibur' | 'publicVote', value: boolean) => {
    if (!isHost || isLocked) return;
    if (key === 'ladyOfLake' || key === 'excalibur') {
      onConfigChange?.({
        ...config,
        extensions: { ...config.extensions, [key]: value },
      });
    } else if (key === 'publicVote') {
      onConfigChange?.({ ...config, publicVote: value });
    }
  };

  const updateForcedRound = (value: RoomConfig['forcedRound']) => {
    if (!isHost || isLocked) return;
    onConfigChange?.({ ...config, forcedRound: value });
  };

  const handleLock = () => {
    if (!isHost) return;
    setConfigSectionCollapsed(true);
    onLockConfig?.();
  };

  const handleUnlock = () => {
    if (!isHost) return;
    setConfigSectionCollapsed(false);
    onUnlockConfig?.(); // 调用父组件的解锁回调
  };

  // 折叠状态显示
  if (configSectionCollapsed) {
    return (
      <div className="avalon-card mb-4 py-2">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <User className="w-4 h-4 text-avalon-gold" />
            游戏配置
          </h3>
          {isHost && (
            <button
              onClick={handleUnlock}
              className="px-2 py-1 rounded-lg text-xs font-medium bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400 transition-colors"
            >
              展开配置
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 游戏人数 - 完全复制线下游戏 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-avalon-text text-sm">游戏人数</div>
          {isLocked && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/30 text-green-400 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              已锁定
            </span>
          )}
        </div>
        <div className="grid grid-cols-5 gap-2 mb-2">
          {[5, 6, 7, 8, 9].map((count) => (
            <button
              key={count}
              onClick={() => updatePlayerCount(count)}
              disabled={isLocked}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${
                config.maxPlayers === count
                  ? 'bg-avalon-gold text-avalon-bg-dark'
                  : 'bg-avalon-card text-white hover:bg-avalon-purple'
              } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {count}人
            </button>
          ))}
        </div>
        <div className="grid grid-cols-5 gap-2 mb-2">
          {[10, 11, 12, 13].map((count) => (
            <button
              key={count}
              onClick={() => updatePlayerCount(count)}
              disabled={isLocked}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${
                config.maxPlayers === count
                  ? 'bg-avalon-gold text-avalon-bg-dark'
                  : 'bg-avalon-card text-white hover:bg-avalon-purple'
              } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {count}人
            </button>
          ))}
          <button
            onClick={isLocked ? handleUnlock : handleLock}
            disabled={!isHost}
            className={`py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
              isLocked
                ? 'bg-amber-500/30 text-amber-400'
                : 'bg-green-500/30 text-green-400'
            } ${!isHost ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLocked ? (
              <>
                <Unlock className="w-4 h-4" />
                <span>解锁</span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                <span>锁定</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 角色配置 - 完全复制线下游戏 */}
      <div className="avalon-card mb-4 py-2">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <User className="w-4 h-4 text-avalon-gold" />
            角色配置
          </h3>
          {isHost && !isLocked && (
            <button
              onClick={() => setShowRoleConfigModal(true)}
              className="px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400"
            >
              <User className="w-3 h-3" />
              自定义配置
            </button>
          )}
        </div>

        <div className="space-y-2">
          {/* 正义阵营 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-blue-400 text-sm font-medium">正义阵营</span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {roleConfigs
                .filter((c) => ROLES[c.roleId].faction === 'good' && c.count > 0)
                .map((roleConfig) => {
                  const role = ROLES[roleConfig.roleId];
                  return (
                    <div
                      key={roleConfig.roleId}
                      className="p-1 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center gap-1"
                    >
                      <span className="text-sm">{role.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-[11px] font-medium truncate">
                          {role.name} <span className="text-avalon-gold">×{roleConfig.count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* 邪恶阵营 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {roleConfigs
                .filter((c) => ROLES[c.roleId].faction === 'evil' && c.count > 0)
                .map((roleConfig) => {
                  const role = ROLES[roleConfig.roleId];
                  return (
                    <div
                      key={roleConfig.roleId}
                      className="p-1 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center gap-1"
                    >
                      <span className="text-sm">{role.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-[11px] font-medium truncate">
                          {role.name} <span className="text-avalon-gold">×{roleConfig.count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* 扩展规则 */}
          <div className="pt-1">
            <div className="flex items-center gap-2 mb-1">
              <Settings className="w-3.5 h-3.5 text-avalon-gold" />
              <span className="text-white font-medium text-xs">扩展规则</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex gap-2">
                <label className="flex-1 flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30 cursor-pointer">
                  <span className="text-white text-xs">启用湖中仙女</span>
                  <input
                    type="checkbox"
                    checked={config.extensions.ladyOfLake}
                    onChange={(e) => updateGameOption('ladyOfLake', e.target.checked)}
                    disabled={isLocked || config.maxPlayers < 8}
                    className="w-3.5 h-3.5 rounded accent-avalon-gold"
                  />
                </label>
                <label className="flex-1 flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30 cursor-pointer">
                  <span className="text-white text-xs">启用王者之剑</span>
                  <input
                    type="checkbox"
                    checked={config.extensions.excalibur}
                    onChange={(e) => updateGameOption('excalibur', e.target.checked)}
                    disabled={isLocked}
                    className="w-3.5 h-3.5 rounded accent-avalon-gold"
                  />
                </label>
              </div>
              
              {/* 明票模式 */}
              <label className="flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30 cursor-pointer">
                <div className="flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 text-avalon-text" />
                  <span className="text-white text-xs">启用明票（所有人可见投票结果）</span>
                </div>
                <input
                  type="checkbox"
                  checked={config.publicVote}
                  onChange={(e) => updateGameOption('publicVote', e.target.checked)}
                  disabled={isLocked}
                  className="w-3.5 h-3.5 rounded accent-avalon-gold"
                />
              </label>

              <div className="flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                <span className="text-white text-xs">强制轮</span>
                <div className="flex rounded-lg bg-avalon-card overflow-hidden border border-avalon-purple/30">
                  <button
                    onClick={() => updateForcedRound('single3')}
                    disabled={isLocked}
                    className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      config.forcedRound === 'single3'
                        ? 'bg-avalon-gold text-avalon-bg-dark'
                        : 'text-white hover:bg-avalon-purple/50'
                    }`}
                  >
                    单轮 3 次
                  </button>
                  <button
                    onClick={() => updateForcedRound('single5')}
                    disabled={isLocked}
                    className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      config.forcedRound === 'single5'
                        ? 'bg-avalon-gold text-avalon-bg-dark'
                        : 'text-white hover:bg-avalon-purple/50'
                    }`}
                  >
                    单轮 5 次
                  </button>
                  <button
                    onClick={() => updateForcedRound('global3')}
                    disabled={isLocked}
                    className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      config.forcedRound === 'global3'
                        ? 'bg-avalon-gold text-avalon-bg-dark'
                        : 'text-white hover:bg-avalon-purple/50'
                    }`}
                  >
                    全局 3 次
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 确认配置并锁定按钮 - 只在未锁定时显示 */}
          {isHost && !isLocked && (
            <button
              onClick={handleLock}
              className="w-full py-2 rounded-xl bg-avalon-gold text-avalon-bg-dark font-semibold text-sm hover:bg-yellow-400 transition-colors mt-1 shadow-lg"
            >
              确认配置并锁定
            </button>
          )}
          
          {/* 已锁定提示 */}
          {isLocked && (
            <div className="w-full py-2 rounded-xl bg-amber-500/20 text-amber-400 font-semibold text-sm text-center mt-1">
              配置已锁定
            </div>
          )}
        </div>
      </div>

      {/* 自定义配置弹窗 */}
      <RoleConfigModal
        isOpen={showRoleConfigModal}
        onClose={() => setShowRoleConfigModal(false)}
        playerCount={config.maxPlayers}
        roleConfigs={roleConfigs}
        onSave={handleRoleConfigSave}
      />
    </>
  );
}
